import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Compass, Eye, EyeOff } from 'lucide-react';

export default function Login({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [legalDocs, setLegalDocs] = useState({
    terms: null,
    privacy: null,
    rules: null
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    birth_date: '',
    accepted_terms: false,
    accepted_privacy: false,
    accepted_rules: false
  });

  useEffect(() => {
    fetchLegalDocuments();
  }, []);

  const fetchLegalDocuments = async () => {
    try {
      const [terms, privacy, rules] = await Promise.all([
        axios.get(`${API}/legal/terms`),
        axios.get(`${API}/legal/privacy`),
        axios.get(`${API}/legal/rules`)
      ]);
      setLegalDocs({
        terms: terms.data,
        privacy: privacy.data,
        rules: rules.data
      });
    } catch (error) {
      console.error('Error loading legal documents:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin) {
      if (!formData.accepted_terms || !formData.accepted_privacy || !formData.accepted_rules) {
        toast.error('Debes aceptar todas las condiciones para registrarte');
        return;
      }
      
      if (formData.birth_date) {
        const datePattern = /^\d{2}-\d{2}-\d{4}$/;
        if (!datePattern.test(formData.birth_date)) {
          toast.error('Formato de fecha incorrecto. Use DD-MM-AAAA');
          return;
        }
      }
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      
      let dataToSend = { ...formData };
      if (!isLogin && formData.birth_date) {
        const [day, month, year] = formData.birth_date.split('-');
        dataToSend.birth_date = `${year}-${month}-${day}`;
      }
      
      const response = await axios.post(`${API}${endpoint}`, dataToSend);
      
      if (response.data.requires_verification) {
        setVerificationData({
          email: response.data.email,
          user_id: response.data.user_id
        });
        setShowVerification(true);
        toast.success('Revisa tu email para el código de verificación');
      } else {
        if (!response.data.user || !response.data.access_token) {
          throw new Error('Respuesta del servidor incompleta');
        }
        onLogin(response.data.user, response.data.access_token);
        toast.success(isLogin ? '¡Bienvenido!' : '¡Cuenta creada exitosamente!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error en la autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-email`, {
        user_id: verificationData.user_id,
        code: verificationCode
      });
      
      onLogin(response.data.user, response.data.access_token);
      toast.success('¡Email verificado exitosamente!');
      setShowVerification(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de verificación inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`, {
        email: verificationData.email
      });
      toast.success('Código reenviado. Revisa tu email');
    } catch (error) {
      toast.error('Error al reenviar código');
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0a0a]">
      {/* Left side - Hero Image (Desktop only) */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://customer-assets-39nsmqrw.emergentagent.net/job_b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/artifacts/5as82tdr_IMG_4642.webp')`
          }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />
        
        {/* Content on image */}
        <div className="relative z-10 flex flex-col justify-between p-12 h-full">
          {/* Top - Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="/logo-icon-new.png" 
              alt="Strategos" 
              className="w-12 h-12 rounded-lg"
            />
          </div>
          
          {/* Center - Tagline */}
          <div className="space-y-4">
            <h1 
              className="text-5xl xl:text-6xl font-black text-white tracking-tight leading-tight"
              style={{ fontFamily: "'Oswald', 'Bebas Neue', sans-serif" }}
            >
              LANDING DE INSCRIPCIÓN<br />
              PARA TUS <span className="text-orange-500">RUTAS</span>,<br />
              <span className="text-orange-500">AVENTURAS</span> Y <span className="text-orange-500">RETOS</span>
            </h1>
            <p className="text-xl text-zinc-300 max-w-md leading-relaxed">
              Únete a la comunidad de aventureros que conquistan cada kilómetro.
            </p>
          </div>
          
          {/* Bottom - Stats or quote */}
          <div className="flex gap-12">
            <div>
              <p className="text-3xl font-bold text-white">500+</p>
              <p className="text-zinc-400 text-sm uppercase tracking-wider">Pilotos</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">50K</p>
              <p className="text-zinc-400 text-sm uppercase tracking-wider">Kilómetros</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">25+</p>
              <p className="text-zinc-400 text-sm uppercase tracking-wider">Eventos</p>
            </div>
          </div>
          
          {/* Partner Organizations */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="text-xs text-zinc-500 uppercase tracking-widest mb-4">Organizadores</p>
            <div className="flex flex-wrap items-center gap-6">
              {/* AEMOTUR */}
              <div className="w-40 h-40 rounded-full overflow-hidden bg-white flex items-center justify-center shadow-lg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/images/3dc092f6f712d30d950b757d2d60ff078a2401883c2245389f056a3132025463.jpeg"
                  alt="AEMOTUR - Asociación Española de Mototurismo"
                  className="w-full h-full object-cover hover:scale-110 transition-transform"
                  title="AEMOTUR - Asociación Española de Mototurismo"
                />
              </div>
              {/* Ride and Feel */}
              <div className="w-40 h-40 rounded-full overflow-hidden bg-white flex items-center justify-center shadow-lg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/images/f7aca28e82199284d91a429230c3dbe426725bf14ce002de212e3873b187fd2a.jpeg"
                  alt="Ride and Feel"
                  className="w-full h-full object-cover hover:scale-110 transition-transform"
                  title="Ride and Feel - EST. 2015"
                />
              </div>
              {/* Dusty Boots */}
              <div className="w-40 h-40 rounded-full overflow-hidden bg-black flex items-center justify-center shadow-lg">
                <img 
                  src="https://customer-assets-39nsmqrw.emergentagent.net/job_b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/artifacts/givxegka_IMG_4540.png"
                  alt="Dusty Boots"
                  className="w-32 h-32 object-contain hover:scale-110 transition-transform"
                  title="Dusty Boots"
                />
              </div>
              {/* Iron Butt Association Iberia */}
              <div className="w-40 h-40 rounded-full overflow-hidden bg-white flex items-center justify-center shadow-lg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/images/8aaab509622a28202ba3f5f54fd32e958cd40af628aec4c10da11595c1b99bea.jpeg"
                  alt="Iron Butt Association Iberia"
                  className="w-full h-full object-cover hover:scale-110 transition-transform"
                  title="Iron Butt Association Iberia - World's Toughest Motorcycle Riders"
                />
              </div>
              {/* AREM - The Asphalt Rats */}
              <div className="w-40 h-40 rounded-full overflow-hidden bg-black flex items-center justify-center shadow-lg">
                <img 
                  src="https://customer-assets-39nsmqrw.emergentagent.net/job_b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/artifacts/azobhumx_AREM%20MAIN%20EUROPE%20COPYRIGHTED%20LOGO%202.PNG"
                  alt="The Asphalt Rats - Endurance Motorcycling Europe"
                  className="w-full h-full object-cover hover:scale-110 transition-transform"
                  title="AREM - The Asphalt Rats"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-[45%] flex flex-col min-h-screen relative bg-white">
        {/* Mobile background */}
        <div 
          className="lg:hidden absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets-39nsmqrw.emergentagent.net/job_b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/artifacts/5as82tdr_IMG_4642.webp')`
          }}
        />
        <div className="lg:hidden absolute inset-0 bg-gradient-to-t from-white via-white/90 to-white/70" />
        
        {/* Subtle orange glow behind form */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-96 h-96 bg-orange-400/15 rounded-full blur-3xl pointer-events-none" />
        
        {/* Form container */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img 
              src="/logo-icon-new.png" 
              alt="Strategos" 
              className="w-16 h-16 rounded-xl mb-4"
              style={{ 
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            />
            <h1 
              className="text-3xl font-black text-gray-900 tracking-wide text-center"
              style={{ 
                fontFamily: "'Oswald', 'Bebas Neue', sans-serif",
                animation: 'fadeInUp 0.8s ease-out 0.2s forwards',
                opacity: 0
              }}
            >
              STRATEGOS OVERLAND
            </h1>
            <p 
              className="text-gray-500 text-sm mt-1"
              style={{ 
                animation: 'fadeInUp 0.8s ease-out 0.4s forwards',
                opacity: 0
              }}
            >
              by Javi Garmendia
            </p>
          </div>

          {/* Desktop title */}
          <div className="hidden lg:block mb-10">
            {/* Strategos Logo */}
            <img 
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_b41f1f79-1fbd-4a20-b119-53b1dfd6f16a/artifacts/lhc511qa_Logo%20de%20Strategos%20Overland%20en%20blanco%20y%20negro%20copia.png"
              alt="Strategos Overland Logo"
              className="w-28 h-28 mb-4"
              style={{ 
                animation: 'fadeInUp 0.6s ease-out forwards',
                filter: 'brightness(0) invert(1) drop-shadow(0 0 10px rgba(255,255,255,0.3))'
              }}
            />
            <h2 
              className="text-4xl font-black text-gray-900 tracking-tight animate-fade-in-up"
              style={{ 
                fontFamily: "'Oswald', 'Bebas Neue', sans-serif",
                animation: 'fadeInUp 0.8s ease-out 0.2s forwards',
                opacity: 0
              }}
            >
              STRATEGOS OVERLAND
            </h2>
            <p 
              className="text-gray-500 mt-2"
              style={{ 
                animation: 'fadeInUp 0.8s ease-out 0.4s forwards',
                opacity: 0
              }}
            >
              by Javi Garmendia
            </p>
          </div>

          <style>{`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes slideInLeft {
              from {
                opacity: 0;
                transform: translateX(-30px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes glowPulse {
              0%, 100% {
                text-shadow: 0 0 5px rgba(249, 115, 22, 0.5);
              }
              50% {
                text-shadow: 0 0 20px rgba(249, 115, 22, 0.8), 0 0 30px rgba(249, 115, 22, 0.4);
              }
            }
          `}</style>

          {/* Tabs */}
          <Tabs defaultValue="login" className="w-full max-w-md" onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid grid-cols-2 gap-2 bg-gray-100 p-1.5 rounded-lg mb-8 h-auto" data-testid="auth-tabs">
              <TabsTrigger 
                value="login" 
                className="font-bold text-sm uppercase tracking-wider py-3 text-gray-600 data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-md transition-all duration-300" 
                data-testid="login-tab"
              >
                Acceder
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="font-bold text-sm uppercase tracking-wider py-3 text-gray-600 data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-md transition-all duration-300" 
                data-testid="register-tab"
              >
                Registrarse
              </TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-gray-700 text-sm font-medium uppercase tracking-wider">
                    Correo Electrónico
                  </Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="piloto@ejemplo.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-14 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 text-base"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-gray-700 text-sm font-medium uppercase tracking-wider">
                    Contraseña
                  </Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="h-14 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 text-base pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <a 
                    href="/forgot-password" 
                    className="text-sm text-orange-500 hover:text-orange-400 transition-colors"
                  >
                    ¿Has olvidado tu contraseña?
                  </a>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 text-base font-bold uppercase tracking-wider bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.4)]"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Compass className="w-5 h-5 animate-spin" />
                      Accediendo...
                    </span>
                  ) : 'ACCEDER'}
                </Button>
              </form>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                <div className="space-y-2">
                  <Label htmlFor="register-name" className="text-gray-700 text-sm font-medium uppercase tracking-wider">
                    Nombre Completo
                  </Label>
                  <Input
                    id="register-name"
                    data-testid="register-name-input"
                    type="text"
                    placeholder="Juan Pérez"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-birth-date" className="text-gray-700 text-sm font-medium uppercase tracking-wider">
                      Fecha Nacimiento
                    </Label>
                    <Input
                      id="register-birth-date"
                      data-testid="register-birth-date-input"
                      type="text"
                      placeholder="DD-MM-AAAA"
                      value={formData.birth_date}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^\d]/g, '');
                        if (value.length >= 2) value = value.slice(0, 2) + '-' + value.slice(2);
                        if (value.length >= 5) value = value.slice(0, 5) + '-' + value.slice(5, 9);
                        setFormData({ ...formData, birth_date: value });
                      }}
                      maxLength={10}
                      required
                      className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-phone" className="text-gray-700 text-sm font-medium uppercase tracking-wider">
                      Teléfono
                    </Label>
                    <Input
                      id="register-phone"
                      data-testid="register-phone-input"
                      type="tel"
                      placeholder="+34 600 000 000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-gray-700 text-sm font-medium uppercase tracking-wider">
                    Correo Electrónico
                  </Label>
                  <Input
                    id="register-email"
                    data-testid="register-email-input"
                    type="email"
                    placeholder="piloto@ejemplo.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-gray-700 text-sm font-medium uppercase tracking-wider">
                    Contraseña
                  </Label>
                  <Input
                    id="register-password"
                    data-testid="register-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                  />
                </div>

                {/* Legal Acceptances */}
                <div className="space-y-3 pt-2 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Aceptaciones obligatorias</p>
                  
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      data-testid="register-accept-terms"
                      checked={formData.accepted_terms}
                      onChange={(e) => setFormData({ ...formData, accepted_terms: e.target.checked })}
                      className="mt-0.5 w-4 h-4 rounded border-gray-400 bg-white text-orange-500 focus:ring-orange-500 focus:ring-offset-0"
                      required
                    />
                    <span className="text-xs text-gray-600 group-hover:text-gray-800 transition-colors">
                      He leído y acepto el{' '}
                      <a href="/aviso-legal" target="_blank" className="text-orange-500 hover:underline">Aviso Legal</a>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      data-testid="register-accept-conditions"
                      checked={formData.accepted_privacy}
                      onChange={(e) => setFormData({ ...formData, accepted_privacy: e.target.checked })}
                      className="mt-0.5 w-4 h-4 rounded border-gray-400 bg-white text-orange-500 focus:ring-orange-500 focus:ring-offset-0"
                      required
                    />
                    <span className="text-xs text-gray-600 group-hover:text-gray-800 transition-colors">
                      He leído y acepto las{' '}
                      <a href="/condiciones-de-uso" target="_blank" className="text-orange-500 hover:underline">Condiciones de Uso</a>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      data-testid="register-accept-privacy"
                      checked={formData.accepted_rules}
                      onChange={(e) => setFormData({ ...formData, accepted_rules: e.target.checked })}
                      className="mt-0.5 w-4 h-4 rounded border-gray-400 bg-white text-orange-500 focus:ring-orange-500 focus:ring-offset-0"
                      required
                    />
                    <span className="text-xs text-gray-600 group-hover:text-gray-800 transition-colors">
                      He leído y acepto la{' '}
                      <a href="/privacidad-y-cookies" target="_blank" className="text-orange-500 hover:underline">Política de Privacidad</a>
                    </span>
                  </label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 text-base font-bold uppercase tracking-wider bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.4)]"
                  disabled={loading || (!formData.accepted_terms || !formData.accepted_privacy || !formData.accepted_rules)}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Compass className="w-5 h-5 animate-spin" />
                      Creando cuenta...
                    </span>
                  ) : 'UNIRSE A LA AVENTURA'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="relative z-10 py-6 px-6 sm:px-12 lg:px-16">
          <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
            <a href="/aviso-legal" className="hover:text-gray-900 transition-colors">
              Aviso Legal
            </a>
            <span className="text-gray-300">|</span>
            <a href="/condiciones-de-uso" className="hover:text-gray-900 transition-colors">
              Condiciones de Uso
            </a>
            <span className="text-gray-300">|</span>
            <a href="/privacidad-y-cookies" className="hover:text-gray-900 transition-colors">
              Privacidad y Cookies
            </a>
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">
            © {new Date().getFullYear()} Strategos Overland. Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* Verification Dialog */}
      <Dialog open={showVerification} onOpenChange={setShowVerification}>
        <DialogContent className="max-w-md bg-white border-gray-200 text-gray-900" data-testid="verification-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Verifica tu Email</DialogTitle>
            <DialogDescription className="text-gray-500">
              Hemos enviado un código de 6 dígitos a {verificationData?.email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVerification} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="verification-code" className="text-gray-700">Código de Verificación</Label>
              <Input
                id="verification-code"
                data-testid="verification-code-input"
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-widest bg-white border-gray-300 text-gray-900"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase"
              disabled={loading || verificationCode.length !== 6}
              data-testid="verify-submit-btn"
            >
              {loading ? 'Verificando...' : 'VERIFICAR EMAIL'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-gray-500 hover:text-gray-900"
              onClick={handleResendCode}
              data-testid="resend-code-btn"
            >
              Reenviar Código
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
